import { html } from 'lit';
import { customElement, property, query, queryAll, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { ColumnDetail, ComplexSelectionFormValue, RowDetail, SelectionType } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { GzSelectableTableRowWebComponent } from './GzSelectableTableRowWebComponent';
import { PropertyValues, TemplateResult } from 'lit/development';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { WEB_COMPONENT_SELECTABLE_TABLE_ROW_GROUP } from '../../../webcomponents.constants';
import { cloneDeep } from 'lodash';
import { map } from 'lit/directives/map.js';
import { keyed } from 'lit/directives/keyed.js';
import { classMap } from 'lit/directives/class-map.js';

@customElement(WEB_COMPONENT_SELECTABLE_TABLE_ROW_GROUP)
export class GzSelectableTableRowGroupWebComponent<Row extends string, Column extends number> extends FormFieldInput {
  /**
   * Set type of selection for the component.
   */
  @property({ type: Number, attribute: true, reflect: true })
  selectionType = SelectionType.RADIO;

  /**
   * Allow row selection. Each row will have a checkbox to select it.
   * A checkbox to select all rows will be displayed in the header.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  allowFullRowSelection = false;

  /**
   * Allow column selection. Each column will have a radio group to select it.
   * A radio to select all columns will be displayed in the header.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  allowFullColumnSelection = false;

  /**
   * Disable radio column selection for all rows.
   * Row selection will be available.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  hasColumnsDisabled = false;

  /**
   * Default column to be selected if value does not contain a column for a row.
   */
  @property({ type: Number, attribute: true, reflect: true })
  defaultColumn: Column;

  /**
   * Columns to be displayed in the header.
   */
  @property({ type: Array, attribute: true, reflect: true })
  columns: ColumnDetail<Column>[] = [];

  /**
   * Rows to be displayed.
   * If any row has availableColumns set, allowFullColumnSelection should be false.
   */
  @property({ type: Array, attribute: true, reflect: true })
  rows: RowDetail<Row, Column>[] = [];

  /**
   * Value of the form.
   */
  @state()
  value: ComplexSelectionFormValue<Row, Column> = {};

  // header of component
  @query('.header')
  headerComponent: GzSelectableTableRowWebComponent<Row, Column>;

  // all rows of component without header
  @queryAll('.row')
  rowComponents: NodeListOf<GzSelectableTableRowWebComponent<Row, Column>>;

  static get styles() {
    return styles;
  }

  /**
   * Get the value of the form. It will return an object with the row as key.
   */
  public getFormValue(): ComplexSelectionFormValue<Row, Column> {
    const formValue: ComplexSelectionFormValue<Row, Column> = {};
    // iterate over all rows and get the value of each row
    this.rowComponents.forEach(rowComponent => {
      formValue[rowComponent.row] = {
        enabled: rowComponent.checked,
        column: rowComponent.value,
      };
    });

    return formValue;
  }

  /**
   * Set the value of the form.
   * @param value
   */
  public setFormValue(value: ComplexSelectionFormValue<Row, Column>): void {
    this.value = cloneDeep(value);
  }

  /**
   * When updated, compute the header checkbox/radio selection.
   * @param changedProperties
   */
  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    this.computeHeaderCheckboxSelection();
    this.computeHeaderRadioSelection();
    this.emitInputEvent();
  }

  /**
   * Render header and rows.
   */
  protected render() {
    const columnsWithoutLabel = this.columns.map(column => {
      return { ...column, label: '' };
    });
    return html` <div class="view-table">${this.renderHeader()} ${this.renderRows(columnsWithoutLabel)}</div> `;
  }

  /**
   * Render the header of the component.
   */
  private renderHeader(): TemplateResult {
    return html`
      <gz-selectable-table-row
        class="view-row header"
        .selectionType="${this.selectionType}"
        ?allowRowSelection="${this.allowFullRowSelection}"
        ?allowColumnSelection="${this.allowFullColumnSelection}"
        ?hasColumnsDisabled="${this.hasColumnsDisabled}"
        .columnDetails="${this.columns}"
        .label="${this.allowFullRowSelection ? translateFn('All') : ''}"
        @row-checkbox-change="${this.handleHeaderCheckboxSelectionChange}"
        @row-value-change="${this.handleHeaderValueSelectionChange}"
      ></gz-selectable-table-row>
    `;
  }

  /**
   * Render the rows of the component based on the rows property.
   * @param columnsWithoutLabel
   */
  private renderRows(columnsWithoutLabel: ColumnDetail<Column>[]): TemplateResult {
    return html`${map(
      this.rows,
      row =>
        html` ${keyed(
          this.value,
          html`<gz-selectable-table-row
            class="view-row row ${classMap({ hidden: row.hidden ?? false })}"
            .selectionType="${this.selectionType}"
            ?allowRowSelection="${this.allowFullRowSelection}"
            ?allowColumnSelection="${true}"
            ?hasColumnsDisabled="${this.hasColumnsDisabled}"
            .row="${row.key}"
            .columnDetails="${columnsWithoutLabel}"
            .label="${row.label}"
            .tooltip="${row.tooltip ?? ''}"
            .availableColumns="${row.availableColumns}"
            ?checked="${this.value[row.key]?.enabled ?? false}"
            ?hidden="${row.hidden ?? false}"
            .value="${this.value[row.key]?.column ?? this.defaultColumn}"
            @row-checkbox-change="${this.handleRowCheckboxSelectionChange}"
            @row-value-change="${this.handleRowValueSelectionChange}"
          ></gz-selectable-table-row> `
        )}`
    )}`;
  }

  /**
   * When a row radio selection changes, recompute header radio selection.
   * @param event
   */
  private handleRowValueSelectionChange(event: CustomEvent) {
    event.stopPropagation();
    this.computeHeaderRadioSelection();
    this.emitInputEvent();
  }

  /**
   * When a header radio selection changes, set all rows to the selected column.
   * @param event
   */
  private handleHeaderValueSelectionChange(event: CustomEvent) {
    event.stopPropagation();
    this.rowComponents.forEach(row => {
      // unchecked or hidden rows should not be modified
      if (!row.checked || row.hidden) {
        return;
      }

      if (this.selectionType === SelectionType.RADIO) {
        row.value = this.headerComponent.value;
        return;
      }

      if (this.selectionType === SelectionType.CHECKBOX) {
        // column is not available for this row
        if (row.availableColumns && !row.availableColumns.includes(event.detail.column)) {
          return;
        }

        // add column to row value
        if (event.detail.checked && !row.value.includes(event.detail.column)) {
          row.value = [...row.value, event.detail.column];
          return;
        }

        // remove column from row value
        if (!event.detail.checked && row.value.includes(event.detail.column)) {
          row.value = row.value.filter(column => column !== event.detail.column);
          return;
        }
      }
    });
    this.emitInputEvent();
  }

  /**
   * When a row checkbox selection changes, recompute header checkbox and radio selection.
   * @param event
   */
  private handleRowCheckboxSelectionChange(event: CustomEvent) {
    event.stopPropagation();

    this.computeHeaderCheckboxSelection();
    this.computeHeaderRadioSelection();
    this.emitInputEvent();
  }

  /**
   * When header checkbox selection changes, set all rows with the same selection and recompute header radio selection.
   * @param event
   */
  private handleHeaderCheckboxSelectionChange(event: CustomEvent) {
    event.stopPropagation();

    this.rowComponents.forEach(row => {
      // hidden rows should not be modified
      if (row.hidden) {
        return;
      }

      row.checked = this.headerComponent.checked;
    });

    this.headerComponent.indeterminate = false;
    this.computeHeaderRadioSelection();
    this.emitInputEvent();
  }

  /**
   * Compute the header checkbox selection based on the rows.
   * - If all rows are checked, header is checked.
   * - If all rows are unchecked, header is unchecked.
   * - If some rows are checked, header is indeterminate.
   */
  private computeHeaderCheckboxSelection() {
    let allChecked = true;
    let allDisabled = true;
    this.rowComponents.forEach(row => {
      // skip hidden rows, because they are not considered in the selection
      if (row.hidden) {
        return;
      }

      allChecked = allChecked && row.checked;
      allDisabled = allDisabled && !row.checked;
    });

    this.headerComponent.checked = allChecked;
    this.headerComponent.indeterminate = !allChecked && !allDisabled;
  }

  /**
   * Compute the header radio selection based on the rows.
   * - If all checked rows have the same column, header is selected with that column.
   * - If checked rows have different columns, header is not selected.
   *
   * Note: Disabled rows are not considered in the selection.
   */
  private computeHeaderRadioSelection() {
    // anything is disabled, then the header is disabled
    if (!this.headerComponent.indeterminate && !this.headerComponent.checked) {
      this.headerComponent.value = null;
      return;
    }

    let hasFullSelection = true;
    let selection;
    this.rowComponents.forEach(row => {
      // skip unchecked and hidden rows, because they are not considered in the selection
      if (!row.checked || row.hidden) {
        return;
      }

      // compute for checkbox selection
      if (this.selectionType === SelectionType.RADIO) {
        // use first selection which is not disabled
        if (selection === undefined) {
          selection = row.value;
        }

        if (selection !== row.value) {
          hasFullSelection = false;
          return;
        }
        return;
      }

      // compute for radio selection
      if (this.selectionType === SelectionType.CHECKBOX) {
        // use first selection which is not disabled
        if (selection === undefined) {
          selection = this.columns.map(column => column.column);
        }

        // filter out columns that are not checked in the row (only if available)
        selection = selection.filter(
          column => (row.value ?? []).includes(column) || !(row.availableColumns ?? selection).includes(column)
        );
        return;
      }
    });

    this.headerComponent.value = hasFullSelection ? selection : null;
  }

  /**
   * Emit change event.
   */
  private emitInputEvent() {
    this.dispatchEvent(
      new CustomEvent('tableRowGroupSelectionChange', { detail: this.getFormValue(), bubbles: true, composed: true })
    );
  }
}
